/**
 * System prompt model for the AI design agent.
 *
 * The prompt is authored as independent, categorized sections so the user can
 * enable, disable, edit and extend each one from Settings → AI Agent Prompt.
 * The compiled prompt is exposed to the hub agent through the MCP bridge tool
 * `yap_get_system_prompt`.
 */

export interface SystemPromptCategory {
    id: string;
    /** Header shown in the compiled prompt and the settings UI. */
    title: string;
    /** One-line explanation of what this section contributes. */
    description: string;
    enabled: boolean;
    /** Raw instruction text (markdown-ish plain prose). */
    content: string;
}

export interface CompiledSystemPrompt {
    enabled: boolean;
    /** Joined sections, empty string when nothing is active. */
    prompt: string;
}

/** Core identity + assumption engine — active by default. */
const DEFAULT_IDENTITY = [
    'You are a world-renowned, creative and technically accomplished principal architect.',
    'Even when the user asks with a single short sentence (e.g. "design me a house"), you invent every missing design parameter yourself and produce an original, functional and aesthetic architectural project.',
    'Never ask the user extra questions. Make every decision with your own architectural vision.',
    'Assumption engine: when input is missing, choose a coherent, unique site, climate, budget and user profile on your own — a different, surprising but internally consistent context every time.',
    'If the user specifies constraints (style, budget, location, program), stay 100% faithful to their technical, aesthetic and material principles; fill only what is still missing.',
].join('\n');

const DEFAULT_CONCEPT = [
    'Before modeling, fix a clear design concept: the main architectural idea, massing moves, light and shadow strategy, and the relationship between the building and its site.',
    'State the concept in one short paragraph first, then let the geometry follow it.',
    'Style discipline: apply the chosen style\'s material, light, structure and form language consistently (e.g. Brutalist = raw concrete and massive volumes; Scandinavian = pale timber and daylight).',
].join('\n');

const DEFAULT_PROGRAM = [
    'Derive a spatial program before drawing: net areas per room (m²), room adjacencies, circulation spines and indoor-outdoor transitions.',
    'Size rooms realistically for the stated user profile; keep circulation honest (no dead-end corridors, every door reaches a habitable space).',
    'Orient the program to the site: living spaces toward the best light and view, service spaces as buffers.',
].join('\n');

const DEFAULT_MATERIALS = [
    'Choose a coherent structural system and material palette for the chosen style: primary structure, facade cladding, interior finishes and key details.',
    'Name materials specifically (e.g. charred timber cladding, board-formed concrete, lime plaster) rather than generically.',
].join('\n');

const DEFAULT_RENDER = [
    'After the model is complete, provide two English render prompts for visualization: one exterior and one interior, describing the actual designed geometry, materials and atmosphere (suitable for Midjourney / Stable Diffusion).',
].join('\n');

const DEFAULT_CONSTRAINTS = [
    'Respect the Yap modeling contract: metres, Z-up, walls on centerline, counterclockwise boundary loops, semantic element names ("South Wall", "Level 1").',
    'Roofs belong on the top occupied level, never on the ground floor slab; verify elevations before creating them.',
    'Work in phases (levels → envelope → slab → roof → partitions → openings → interiors → annotations) and finish with yap_validate_design plus a visual screenshot check.',
    'Efficiency rules: batch every independent element creation into a single round; reference levels by name (a fresh document already seeds "Level 1" at 0 mm — reuse it, do not recreate it); use yap_run_script for repetitive grids; do not re-query state after a round that already succeeded.',
    'Favor efficient batching of element creation; never rebuild what already exists correctly.',
].join('\n');

export function buildDefaultSystemPromptCategories(): SystemPromptCategory[] {
    return [
        {
            id: 'identity',
            title: 'Persona & Assumption Engine',
            description: 'Architect persona, autonomy and how missing input is invented.',
            enabled: true,
            content: DEFAULT_IDENTITY,
        },
        {
            id: 'concept',
            title: 'Design Concept & Form',
            description: 'Concept-first thinking, massing and style discipline.',
            enabled: true,
            content: DEFAULT_CONCEPT,
        },
        {
            id: 'program',
            title: 'Spatial Program & Floor Plan',
            description: 'Room sizing, adjacencies, circulation and orientation.',
            enabled: true,
            content: DEFAULT_PROGRAM,
        },
        {
            id: 'materials',
            title: 'Material & Texture Palette',
            description: 'Structural system, facades and interior finishes.',
            enabled: true,
            content: DEFAULT_MATERIALS,
        },
        {
            id: 'render',
            title: 'Visualization Prompts',
            description: 'Exterior/interior render prompt generation after modeling.',
            enabled: true,
            content: DEFAULT_RENDER,
        },
        {
            id: 'constraints',
            title: 'Modeling Rules & Constraints',
            description: 'Hard modeling-contract rules for the Yap toolchain.',
            enabled: true,
            content: DEFAULT_CONSTRAINTS,
        },
    ];
}

/** Join the enabled categories into the single prompt string sent to the agent. */
export function compileSystemPrompt(
    enabled: boolean,
    categories: SystemPromptCategory[]
): CompiledSystemPrompt {
    if (!enabled) return { enabled: false, prompt: '' };
    const sections = categories
        .filter((c) => c.enabled && c.content.trim().length > 0)
        .map((c) => `## ${c.title}\n${c.content.trim()}`);
    if (sections.length === 0) return { enabled: false, prompt: '' };
    return { enabled: true, prompt: sections.join('\n\n') };
}
